import {
  keepPreviousData,
  useQuery,
  UseQueryResult,
} from '@tanstack/react-query';

import { useAppContext } from '../../contexts/Storage';
import { AccountListItem } from '../../models/AccountListItem';
import { accountsService } from '../../services/accountsService';

// ---------------------------------------------------------------------------
// Query keys & types
// ---------------------------------------------------------------------------

export const LEDGERS_QUERY_KEY = ['ledgers'] as const;
export const STOCK_VALUATION_QUERY_KEY = ['stock-valuation'] as const;
export const TRADING_ACCOUNTS_QUERY_KEY = ['trading-accounts'] as const;

export interface LedgersPagination {
  page: number;
  perPage: number;
  total: number;
  lastPage: number;
  from: number | null;
  to: number | null;
}

export interface LedgersResponse {
  ledgers: AccountListItem[];
  pagination: LedgersPagination;
}

export interface LedgerFetchFilter {
  record_id: number;
  user_id: number;
  per_page?: number;
  page?: number;
  start_date?: string;
  end_date?: string;
}

// ---------------------------------------------------------------------------
// API Response Types (matching actual API response structure)
// ---------------------------------------------------------------------------

interface LedgerApiItem {
  id: number;
  transaction_entity_name: string;
  transaction_entity_description: string | null;
  transaction_entity_category: string;
  transaction_entity_type: string | null;
  transaction_entity_past_tense: string | null;
  is_exclusive: number;
  transaction_entity_balance_amount: number;
  transaction_entity_balance_quantity: number;
  transaction_entity_balance_type: string;
}

interface LedgerListApiResponse {
  current_page: number;
  data: LedgerApiItem[];
  first_page_url: string;
  from: number | null;
  last_page: number;
  last_page_url: string;
  links: { url: string | null; label: string; active: boolean }[];
  next_page_url: string | null;
  path: string;
  per_page: number;
  prev_page_url: string | null;
  to: number | null;
  total: number;
}

// ---------------------------------------------------------------------------
// Mapper functions
// ---------------------------------------------------------------------------

function mapApiLedgerToAccountListItem(apiItem: LedgerApiItem): AccountListItem {
  return new AccountListItem({
    id: apiItem.id,
    transaction_entity_name: apiItem.transaction_entity_name,
    transaction_entity_description: apiItem.transaction_entity_description ?? undefined,
    transaction_entity_category: apiItem.transaction_entity_category,
    transaction_entity_type: apiItem.transaction_entity_type ?? undefined,
    transaction_entity_past_tense: apiItem.transaction_entity_past_tense ?? undefined,
    is_exclusive: apiItem.is_exclusive,
    transaction_entity_balance_amount: apiItem.transaction_entity_balance_amount,
    transaction_entity_balance_quantity: apiItem.transaction_entity_balance_quantity,
    transaction_entity_balance_type: apiItem.transaction_entity_balance_type,
  });
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

export interface UseGetLedgersParams {
  filters?: Partial<LedgerFetchFilter>;
  enabled?: boolean;
}

/**
 * Fetches ledger accounts for the current user with optional filters.
 * Requires record_id and user_id in filters.
 */
export function useGetLedgers({
  filters,
  enabled = true,
}: UseGetLedgersParams = {}): UseQueryResult<LedgersResponse, Error> {
  const { token, user, selectedRecord } = useAppContext();

  const recordId = filters?.record_id ?? selectedRecord?.id;
  const userId = filters?.user_id ?? user?.id;

  return useQuery({
    queryKey: [...LEDGERS_QUERY_KEY, recordId, userId, filters],
    queryFn: async () => {
      if (!token || !userId || !recordId) {
        throw new Error('User not authenticated or no record selected');
      }

      const requestBody = {
        record_id: recordId,
        user_id: userId,
        ...(filters?.per_page && { per_page: filters.per_page }),
        ...(filters?.page && { page: filters.page }),
        ...(filters?.start_date && { start_date: filters.start_date }),
        ...(filters?.end_date && { end_date: filters.end_date }),
      };

      const result = await accountsService.fetchLedgerAccounts(token, requestBody as any);

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch ledgers');
      }

      // The service returns the raw API response - we need to map it
      // The response comes directly as the paginated structure
      const apiResponse = result.data as unknown as LedgerListApiResponse;

      const rawData = apiResponse?.data ?? [];
      const ledgers = rawData.map(mapApiLedgerToAccountListItem);

      return {
        ledgers,
        pagination: {
          page: apiResponse?.current_page ?? 1,
          perPage: apiResponse?.per_page ?? 15,
          total: apiResponse?.total ?? 0,
          lastPage: apiResponse?.last_page ?? 1,
          from: apiResponse?.from ?? null,
          to: apiResponse?.to ?? null,
        },
      };
    },
    enabled: enabled && !!token && !!userId && !!recordId,
    placeholderData: keepPreviousData,
  });
}

// ---------------------------------------------------------------------------
// Stock Valuation Types
// ---------------------------------------------------------------------------

export interface StockValuationResponse {
  stocks: AccountListItem[];
  pagination: LedgersPagination;
}

export interface UseGetStockValuationParams {
  filters?: Partial<LedgerFetchFilter>;
  enabled?: boolean;
}

/**
 * Fetches stock valuation accounts for the current user with optional filters.
 * Requires record_id and user_id in filters.
 */
export function useGetStockValuation({
  filters,
  enabled = true,
}: UseGetStockValuationParams = {}): UseQueryResult<StockValuationResponse, Error> {
  const { token, user, selectedRecord } = useAppContext();

  const recordId = filters?.record_id ?? selectedRecord?.id;
  const userId = filters?.user_id ?? user?.id;

  return useQuery({
    queryKey: [...STOCK_VALUATION_QUERY_KEY, recordId, userId, filters],
    queryFn: async () => {
      if (!token || !userId || !recordId) {
        throw new Error('User not authenticated or no record selected');
      }

      const requestBody = {
        record_id: recordId,
        user_id: userId,
        ...(filters?.per_page && { per_page: filters.per_page }),
        ...(filters?.page && { page: filters.page }),
        ...(filters?.start_date && { start_date: filters.start_date }),
        ...(filters?.end_date && { end_date: filters.end_date }),
      };

      const result = await accountsService.fetchStockValuationAccounts(token, requestBody as any);

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch stock valuation');
      }

      const apiResponse = result.data as unknown as LedgerListApiResponse;

      const rawData = apiResponse?.data ?? [];
      const stocks = rawData.map(mapApiLedgerToAccountListItem);

      return {
        stocks,
        pagination: {
          page: apiResponse?.current_page ?? 1,
          perPage: apiResponse?.per_page ?? 15,
          total: apiResponse?.total ?? 0,
          lastPage: apiResponse?.last_page ?? 1,
          from: apiResponse?.from ?? null,
          to: apiResponse?.to ?? null,
        },
      };
    },
    enabled: enabled && !!token && !!userId && !!recordId,
    placeholderData: keepPreviousData,
  });
}

// ---------------------------------------------------------------------------
// Trading Accounts Types and Hook
// ---------------------------------------------------------------------------

export interface TradingAccountsFetchFilter {
  record_id?: number;
  user_id?: number;
  per_page?: number;
  page?: number;
}

export interface TradingAccountsResponse {
  accounts: AccountListItem[];
  pagination: LedgersPagination;
}

export interface UseGetTradingAccountsParams {
  filters?: Partial<TradingAccountsFetchFilter>;
  enabled?: boolean;
}

/**
 * Fetches trading accounts for the current user with optional filters.
 * Requires record_id and user_id in filters.
 */
export function useGetTradingAccounts({
  filters,
  enabled = true,
}: UseGetTradingAccountsParams = {}): UseQueryResult<TradingAccountsResponse, Error> {
  const { token, user, selectedRecord } = useAppContext();

  const recordId = filters?.record_id ?? selectedRecord?.id;
  const userId = filters?.user_id ?? user?.id;

  return useQuery({
    queryKey: [...TRADING_ACCOUNTS_QUERY_KEY, recordId, userId, filters],
    queryFn: async () => {
      if (!token || !userId || !recordId) {
        throw new Error('User not authenticated or no record selected');
      }

      const requestBody = {
        record_id: recordId,
        user_id: userId,
        ...(filters?.per_page && { per_page: filters.per_page }),
        ...(filters?.page && { page: filters.page }),
      };

      const result = await accountsService.fetchTradingAccounts(token, requestBody as any);

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch trading accounts');
      }

      const apiResponse = result.data as unknown as LedgerListApiResponse;

      const rawData = apiResponse?.data ?? [];
      const accounts = rawData.map(mapApiLedgerToAccountListItem);

      return {
        accounts,
        pagination: {
          page: apiResponse?.current_page ?? 1,
          perPage: apiResponse?.per_page ?? 15,
          total: apiResponse?.total ?? 0,
          lastPage: apiResponse?.last_page ?? 1,
          from: apiResponse?.from ?? null,
          to: apiResponse?.to ?? null,
        },
      };
    },
    enabled: enabled && !!token && !!userId && !!recordId,
    placeholderData: keepPreviousData,
  });
}

// ---------------------------------------------------------------------------
// Single Stock Valuation Detail Types
// ---------------------------------------------------------------------------

export const SINGLE_STOCK_VALUATION_QUERY_KEY = ['single-stock-valuation'] as const;

export interface SingleStockValuationFilter {
  entity_id: number;
  record_id: number;
  user_id: number;
  start_date?: string;
  end_date?: string;
  per_page?: number;
  page?: number;
}

interface TransactionEntityApi {
  id: number;
  transaction_entity_name: string;
  transaction_entity_description: string | null;
  transaction_entity_category: string;
  transaction_entity_type: string | null;
  transaction_entity_past_tense: string | null;
}

interface StockValuationTransactionApi {
  id: number;
  transaction_record_id: number;
  transaction_date: string;
  transaction_action_id: number;
  transaction_object_id: number;
  transaction_indirect_object_id: number | null;
  transaction_media_id: number;
  transaction_quantity: number;
  transaction_total_amount: number;
  created_by_user_id: number;
  action?: TransactionEntityApi;
  object?: TransactionEntityApi;
  indirect_object?: TransactionEntityApi | null;
  media?: TransactionEntityApi;
}

interface StockValuationItemApi {
  transaction: StockValuationTransactionApi;
  balance_amount: number;
  balance_quantity: number;
  balance_type: string;
}

interface StockValuationDetailApiResponse {
  current_page: number;
  data: StockValuationItemApi[];
  first_page_url: string;
  from: number | null;
  last_page: number;
  last_page_url: string;
  links: { url: string | null; label: string; active: boolean }[];
  next_page_url: string | null;
  path: string;
  per_page: number;
  prev_page_url: string | null;
  to: number | null;
  total: number;
}

export interface StockValuationDetailItem {
  id: number;
  date: string;
  isReceipt: boolean;
  quantity: number;
  rate: number;
  amount: number;
  balanceQuantity: number;
  balanceRate: number;
  balanceAmount: number;
  actionName?: string;
  objectName?: string;
  indirectObjectName?: string;
  mediaName?: string;
}

export interface SingleStockValuationResponse {
  items: StockValuationDetailItem[];
  pagination: LedgersPagination;
}

export interface UseGetSingleStockValuationParams {
  filters?: Partial<SingleStockValuationFilter>;
  enabled?: boolean;
}

function mapStockValuationItem(apiItem: StockValuationItemApi): StockValuationDetailItem {
  const transaction = apiItem.transaction;
  const isReceipt = transaction.action?.transaction_entity_type === 'debit';
  const quantity = transaction.transaction_quantity;
  const amount = transaction.transaction_total_amount;
  const rate = quantity !== 0 ? amount / quantity : 0;

  const balanceQuantity = apiItem.balance_quantity;
  const balanceAmount = apiItem.balance_amount;
  const balanceRate = balanceQuantity !== 0 ? balanceAmount / balanceQuantity : 0;

  return {
    id: transaction.id,
    date: transaction.transaction_date,
    isReceipt,
    quantity,
    rate,
    amount,
    balanceQuantity,
    balanceRate,
    balanceAmount,
    actionName: transaction.action?.transaction_entity_name,
    objectName: transaction.object?.transaction_entity_name,
    indirectObjectName: transaction.indirect_object?.transaction_entity_name,
    mediaName: transaction.media?.transaction_entity_name,
  };
}

/**
 * Fetches single stock valuation details for a specific entity.
 * Requires entity_id, record_id, and user_id in filters.
 */
export function useGetSingleStockValuation({
  filters,
  enabled = true,
}: UseGetSingleStockValuationParams = {}): UseQueryResult<SingleStockValuationResponse, Error> {
  const { token, user, selectedRecord } = useAppContext();

  const entityId = filters?.entity_id;
  const recordId = filters?.record_id ?? selectedRecord?.id;
  const userId = filters?.user_id ?? user?.id;

  return useQuery({
    queryKey: [...SINGLE_STOCK_VALUATION_QUERY_KEY, entityId, recordId, userId, filters],
    queryFn: async () => {
      if (!token || !userId || !recordId || !entityId) {
        throw new Error('User not authenticated, no record selected, or no entity specified');
      }

      const requestBody = {
        entity_id: entityId,
        record_id: recordId,
        user_id: userId,
        ...(filters?.per_page && { per_page: filters.per_page }),
        ...(filters?.page && { page: filters.page }),
        ...(filters?.start_date && { start_date: filters.start_date }),
        ...(filters?.end_date && { end_date: filters.end_date }),
      };

      const result = await accountsService.fetchStockValuationDetails(token, requestBody as any);

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch stock valuation details');
      }

      const apiResponse = result.data as unknown as StockValuationDetailApiResponse;

      const rawData = apiResponse?.data ?? [];
      const items = rawData.map(mapStockValuationItem);

      return {
        items,
        pagination: {
          page: apiResponse?.current_page ?? 1,
          perPage: apiResponse?.per_page ?? 15,
          total: apiResponse?.total ?? 0,
          lastPage: apiResponse?.last_page ?? 1,
          from: apiResponse?.from ?? null,
          to: apiResponse?.to ?? null,
        },
      };
    },
    enabled: enabled && !!token && !!userId && !!recordId && !!entityId,
    placeholderData: keepPreviousData,
  });
}

// ---------------------------------------------------------------------------
// Overall Stock Valuation Types
// ---------------------------------------------------------------------------

export const OVERALL_STOCK_VALUATION_QUERY_KEY = ['overall-stock-valuation'] as const;

export interface OverallStockValuationFilter {
  record_id: number;
  user_id: number;
  start_date: string;
  end_date: string;
  per_page?: number;
  page?: number;
}

export interface UseGetOverallStockValuationParams {
  filters?: Partial<OverallStockValuationFilter>;
  enabled?: boolean;
}

/**
 * Fetches overall stock valuation details (all items combined).
 * Requires record_id, user_id, start_date, and end_date in filters.
 */
export function useGetOverallStockValuation({
  filters,
  enabled = true,
}: UseGetOverallStockValuationParams = {}): UseQueryResult<SingleStockValuationResponse, Error> {
  const { token, user, selectedRecord } = useAppContext();

  const recordId = filters?.record_id ?? selectedRecord?.id;
  const userId = filters?.user_id ?? user?.id;

  return useQuery({
    queryKey: [...OVERALL_STOCK_VALUATION_QUERY_KEY, recordId, userId, filters],
    queryFn: async () => {
      if (!token || !userId || !recordId) {
        throw new Error('User not authenticated or no record selected');
      }

      if (!filters?.start_date || !filters?.end_date) {
        throw new Error('Start date and end date are required');
      }

      const requestBody = {
        record_id: recordId,
        user_id: userId,
        start_date: filters.start_date,
        end_date: filters.end_date,
        ...(filters?.per_page && { per_page: filters.per_page }),
        ...(filters?.page && { page: filters.page }),
      };

      const result = await accountsService.fetchOverallStockValuationDetails(token, requestBody as any);

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch overall stock valuation');
      }

      const apiResponse = result.data as unknown as StockValuationDetailApiResponse;

      const rawData = apiResponse?.data ?? [];
      const items = rawData.map(mapStockValuationItem);

      return {
        items,
        pagination: {
          page: apiResponse?.current_page ?? 1,
          perPage: apiResponse?.per_page ?? 15,
          total: apiResponse?.total ?? 0,
          lastPage: apiResponse?.last_page ?? 1,
          from: apiResponse?.from ?? null,
          to: apiResponse?.to ?? null,
        },
      };
    },
    enabled: enabled && !!token && !!userId && !!recordId && !!filters?.start_date && !!filters?.end_date,
    placeholderData: keepPreviousData,
  });
}

// ---------------------------------------------------------------------------
// Balance Sheet Types
// ---------------------------------------------------------------------------

export const BALANCE_SHEET_QUERY_KEY = ['balance-sheet'] as const;

export interface BalanceSheetFilter {
  record_id: number;
  user_id: number;
  start_date: string;
  end_date: string;
}

// Assets can have nested objects (like media) or direct numbers (like stock, prepaid)
export interface BalanceSheetAssets {
  media?: Record<string, number>;
  stock?: number;
  prepaid?: number;
  debitors?: number;
  // Allow any additional dynamic fields
  [key: string]: Record<string, number> | number | undefined;
}

export interface BalanceSheetApiResponse {
  record_id: number;
  start_date: string;
  end_date: string;
  assets: BalanceSheetAssets;
  liabilities: Record<string, number>;
  capital: Record<string, number>;
}

export interface BalanceSheetResponse {
  data: BalanceSheetApiResponse;
}

export interface UseGetBalanceSheetParams {
  filters?: Partial<BalanceSheetFilter>;
  enabled?: boolean;
}

/**
 * Fetches balance sheet data.
 * Requires record_id, user_id, start_date, and end_date in filters.
 */
export function useGetBalanceSheet({
  filters,
  enabled = true,
}: UseGetBalanceSheetParams = {}): UseQueryResult<BalanceSheetResponse, Error> {
  const { token, user, selectedRecord } = useAppContext();

  const recordId = filters?.record_id ?? selectedRecord?.id;
  const userId = filters?.user_id ?? user?.id;

  return useQuery({
    queryKey: [...BALANCE_SHEET_QUERY_KEY, recordId, userId, filters],
    queryFn: async () => {
      if (!token || !userId || !recordId) {
        throw new Error('User not authenticated or no record selected');
      }

      if (!filters?.start_date || !filters?.end_date) {
        throw new Error('Start date and end date are required');
      }

      const requestBody = {
        record_id: recordId,
        user_id: userId,
        start_date: filters.start_date,
        end_date: filters.end_date,
      };

      const result = await accountsService.fetchBalanceSheet(token, requestBody as any);

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch balance sheet');
      }

      return {
        data: result.data as unknown as BalanceSheetApiResponse,
      };
    },
    enabled: enabled && !!token && !!userId && !!recordId && !!filters?.start_date && !!filters?.end_date,
    placeholderData: keepPreviousData,
  });
}

// ---------------------------------------------------------------------------
// Profit and Loss Types
// ---------------------------------------------------------------------------

export const PROFIT_AND_LOSS_QUERY_KEY = ['profit-and-loss'] as const;

export interface ProfitAndLossFilter {
  record_id: number;
  user_id: number;
  start_date: string;
  end_date: string;
}

export interface ProfitAndLossApiResponse {
  record_id: number;
  start_date: string;
  end_date: string;
  debit: Record<string, number | string>;
  credit: Record<string, number | string>;
  total_debit: number;
  total_credit: number;
}

export interface ProfitAndLossResponse {
  data: ProfitAndLossApiResponse;
}

export interface UseGetProfitAndLossParams {
  filters?: Partial<ProfitAndLossFilter>;
  enabled?: boolean;
}

/**
 * Fetches profit and loss data.
 * Requires record_id, user_id, start_date, and end_date in filters.
 */
export function useGetProfitAndLoss({
  filters,
  enabled = true,
}: UseGetProfitAndLossParams = {}): UseQueryResult<ProfitAndLossResponse, Error> {
  const { token, user, selectedRecord } = useAppContext();

  const recordId = filters?.record_id ?? selectedRecord?.id;
  const userId = filters?.user_id ?? user?.id;

  return useQuery({
    queryKey: [...PROFIT_AND_LOSS_QUERY_KEY, recordId, userId, filters],
    queryFn: async () => {
      if (!token || !userId || !recordId) {
        throw new Error('User not authenticated or no record selected');
      }

      if (!filters?.start_date || !filters?.end_date) {
        throw new Error('Start date and end date are required');
      }

      const requestBody = {
        record_id: recordId,
        user_id: userId,
        start_date: filters.start_date,
        end_date: filters.end_date,
      };

      const result = await accountsService.fetchProfitAndLoss(token, requestBody as any);

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch profit and loss');
      }

      return {
        data: result.data as unknown as ProfitAndLossApiResponse,
      };
    },
    enabled: enabled && !!token && !!userId && !!recordId && !!filters?.start_date && !!filters?.end_date,
    placeholderData: keepPreviousData,
  });
}

// ---------------------------------------------------------------------------
// Overall Trading Account Types
// ---------------------------------------------------------------------------

export const OVERALL_TRADING_ACCOUNT_QUERY_KEY = ['overall-trading-account'] as const;

export interface OverallTradingAccountFilter {
  record_id: number;
  user_id: number;
  start_date: string;
  end_date: string;
}

export interface OverallTradingAccountApiResponse {
  success: boolean;
  record_id: number;
  start_date: string;
  end_date: string;
  debit: Record<string, number | string>;
  credit: Record<string, number | string>;
  balance_dr: number;
  balance_cr: number;
}

export interface OverallTradingAccountResponse {
  data: OverallTradingAccountApiResponse;
}

export interface UseGetOverallTradingAccountParams {
  filters?: Partial<OverallTradingAccountFilter>;
  enabled?: boolean;
}

/**
 * Fetches overall trading account data.
 * Requires record_id, user_id, start_date, and end_date in filters.
 */
export function useGetOverallTradingAccount({
  filters,
  enabled = true,
}: UseGetOverallTradingAccountParams = {}): UseQueryResult<OverallTradingAccountResponse, Error> {
  const { token, user, selectedRecord } = useAppContext();

  const recordId = filters?.record_id ?? selectedRecord?.id;
  const userId = filters?.user_id ?? user?.id;

  return useQuery({
    queryKey: [...OVERALL_TRADING_ACCOUNT_QUERY_KEY, recordId, userId, filters],
    queryFn: async () => {
      if (!token || !userId || !recordId) {
        throw new Error('User not authenticated or no record selected');
      }

      if (!filters?.start_date || !filters?.end_date) {
        throw new Error('Start date and end date are required');
      }

      const requestBody = {
        record_id: recordId,
        user_id: userId,
        start_date: filters.start_date,
        end_date: filters.end_date,
      };

      const result = await accountsService.fetchOverallTradingAccountDetails(token, requestBody as any);

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch overall trading account');
      }

      return {
        data: result.data as unknown as OverallTradingAccountApiResponse,
      };
    },
    enabled: enabled && !!token && !!userId && !!recordId && !!filters?.start_date && !!filters?.end_date,
    placeholderData: keepPreviousData,
  });
}

// ---------------------------------------------------------------------------
// Single Trading Account Detail Types
// ---------------------------------------------------------------------------

export const SINGLE_TRADING_ACCOUNT_QUERY_KEY = ['single-trading-account'] as const;

export interface SingleTradingAccountFilter {
  entity_id: number;
  record_id: number;
  user_id: number;
  start_date: string;
  end_date: string;
  per_page?: number;
}

export interface SingleTradingAccountApiResponse {
  entity_id: number;
  start_date: string;
  end_date: string;
  debit: Record<string, number | string>;
  credit: Record<string, number | string>;
  balance_dr: number;
  balance_cr: number;
}

export interface SingleTradingAccountResponse {
  data: SingleTradingAccountApiResponse;
}

export interface UseGetSingleTradingAccountParams {
  filters?: Partial<SingleTradingAccountFilter>;
  enabled?: boolean;
}

/**
 * Fetches a single trading account detail (debit/credit breakdown).
 * Requires entity_id, record_id, user_id, start_date, and end_date.
 */
export function useGetSingleTradingAccount({
  filters,
  enabled = true,
}: UseGetSingleTradingAccountParams = {}): UseQueryResult<SingleTradingAccountResponse, Error> {
  const { token, user, selectedRecord } = useAppContext();

  const entityId = filters?.entity_id;
  const recordId = filters?.record_id ?? selectedRecord?.id;
  const userId = filters?.user_id ?? user?.id;

  return useQuery({
    queryKey: [...SINGLE_TRADING_ACCOUNT_QUERY_KEY, entityId, recordId, userId, filters],
    queryFn: async () => {
      if (!token || !userId || !recordId || !entityId) {
        throw new Error('User not authenticated, no record selected, or no entity specified');
      }

      if (!filters?.start_date || !filters?.end_date) {
        throw new Error('Start date and end date are required');
      }

      const requestBody = {
        entity_id: entityId,
        record_id: recordId,
        user_id: userId,
        start_date: filters.start_date,
        end_date: filters.end_date,
        ...(filters?.per_page && { per_page: filters.per_page }),
      };

      const result = await accountsService.fetchTradingAccountDetails(token, requestBody as any);

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch trading account details');
      }

      return {
        data: result.data as unknown as SingleTradingAccountApiResponse,
      };
    },
    enabled: enabled && !!token && !!userId && !!recordId && !!entityId && !!filters?.start_date && !!filters?.end_date,
    placeholderData: keepPreviousData,
  });
}

// ---------------------------------------------------------------------------
// Single Ledger Detail Types
// ---------------------------------------------------------------------------

export const SINGLE_LEDGER_QUERY_KEY = ['single-ledger'] as const;

export interface SingleLedgerFilter {
  entity_id: number;
  record_id: number;
  user_id: number;
  start_date: string;
  end_date: string;
  per_page?: number;
  page?: number;
}

export interface LedgerTransactionEntity {
  id: number;
  transaction_entity_name: string;
  transaction_entity_description: string | null;
  transaction_entity_category: string;
  transaction_entity_type: string | null;
  transaction_entity_past_tense: string | null;
}

export interface LedgerTransaction {
  id: number;
  transaction_record_id: number;
  transaction_date: string;
  transaction_action_id: number;
  transaction_object_id: number;
  transaction_indirect_object_id: number | null;
  transaction_media_id: number;
  transaction_quantity: number;
  transaction_total_amount: number;
  created_by_user_id: number;
  action: LedgerTransactionEntity;
  object: LedgerTransactionEntity;
  indirect_object: LedgerTransactionEntity | null;
  media: LedgerTransactionEntity;
}

export interface SingleLedgerApiResponse {
  start_date: string;
  end_date: string;
  entity_id: number;
  balance_amount: number;
  balance_quantity: number;
  balance_type: string;
  credit_transactions: LedgerTransaction[];
  debit_transactions: LedgerTransaction[];
}

export interface SingleLedgerResponse {
  data: SingleLedgerApiResponse;
}

export interface UseGetSingleLedgerParams {
  filters?: Partial<SingleLedgerFilter>;
  enabled?: boolean;
}

export function useGetSingleLedger({
  filters,
  enabled = true,
}: UseGetSingleLedgerParams = {}): UseQueryResult<SingleLedgerResponse, Error> {
  const { token, user, selectedRecord } = useAppContext();

  const entityId = filters?.entity_id;
  const recordId = filters?.record_id ?? selectedRecord?.id;
  const userId = filters?.user_id ?? user?.id;

  return useQuery({
    queryKey: [...SINGLE_LEDGER_QUERY_KEY, entityId, recordId, userId, filters],
    queryFn: async () => {
      if (!token || !userId || !recordId || !entityId) {
        throw new Error('User not authenticated, no record selected, or no entity specified');
      }

      const requestBody = {
        entity_id: entityId,
        record_id: recordId,
        user_id: userId,
        start_date: filters!.start_date,
        end_date: filters!.end_date,
        ...(filters?.per_page && { per_page: filters.per_page }),
        ...(filters?.page && { page: filters.page }),
      };

      const result = await accountsService.fetchLedgerDetail(token, requestBody as any);

      if (!result.success) {
        throw new Error(result.error || 'Failed to fetch ledger details');
      }

      return {
        data: result.data as unknown as SingleLedgerApiResponse,
      };
    },
    enabled: enabled && !!token && !!userId && !!recordId && !!entityId && !!filters?.start_date && !!filters?.end_date,
    placeholderData: keepPreviousData,
  });
}

// Re-export types for convenience
export type { AccountListItem };
